import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { AuthProvider } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { MainLayout } from '@/components/layout/MainLayout';

// Lazy load pages for better performance
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const JobsPage = lazy(() => import('@/pages/jobs/JobsPage'));
const JobDetailPage = lazy(() => import('@/pages/jobs/JobDetailPage'));
const ApplicationsPage = lazy(() => import('@/pages/applications/ApplicationsPage'));
const SurveysPage = lazy(() => import('@/pages/surveys/SurveysPage'));
const SurveyBuilderPage = lazy(() => import('@/pages/surveys/SurveyBuilderPage'));
const AdminPanelPage = lazy(() => import('@/pages/admin/AdminPanelPage'));
const PublicApplicationPage = lazy(() => import('@/pages/public/PublicApplicationPage'));
const PublicSurveyPage = lazy(() => import('@/pages/public/PublicSurveyPage'));
const MultiStepRegistrationPage = lazy(() => import('@/pages/public/MultiStepRegistrationPage'));
const PublicQuizPage = lazy(() => import('@/pages/public/PublicQuizPage'));
const SurveyLandingPage = lazy(() => import('@/pages/public/SurveyLandingPage'));
const CandidaturePage = lazy(() => import('@/pages/public/CandidaturePage'));
const CandidatureFormPage = lazy(() => import('@/pages/public/CandidatureFormPage'));
const QuizzesPage = lazy(() => import('@/pages/quizzes/QuizzesPage'));
const QuizDetailPage = lazy(() => import('@/pages/quizzes/QuizDetailPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

function App() {
  return (
    <AuthProvider>
      <Suspense fallback={<LoadingSpinner fullScreen />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/register/multistep" element={<MultiStepRegistrationPage />} />
          <Route path="/register-steps" element={<MultiStepRegistrationPage />} />
          <Route path="/register-member" element={<MultiStepRegistrationPage />} />
          <Route path="/signup/steps" element={<MultiStepRegistrationPage />} />
          <Route path="/survey" element={<SurveyLandingPage />} />
          <Route path="/apply/:token" element={<PublicApplicationPage />} />
          <Route path="/survey/:token" element={<PublicSurveyPage />} />
          <Route path="/quiz/:token" element={<PublicQuizPage />} />
          <Route path="/candidature" element={<CandidaturePage />} />
          <Route path="/candidature/postuler" element={<CandidatureFormPage />} />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="jobs" element={<JobsPage />} />
            <Route path="jobs/:id" element={<JobDetailPage />} />
            <Route path="applications" element={<ApplicationsPage />} />
            <Route path="surveys" element={<SurveysPage />} />
            <Route path="surveys/new" element={<SurveyBuilderPage />} />
            <Route path="surveys/:id/edit" element={<SurveyBuilderPage />} />
            <Route path="quizzes" element={<QuizzesPage />} />
            <Route path="quizzes/:quizId" element={<QuizDetailPage />} />
            
            {/* Admin Routes */}
            <Route
              path="admin/*"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <AdminPanelPage />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      <Toaster />
    </AuthProvider>
  );
}

export default App;
